import React, { useEffect } from 'react';
import useToolStore, { TOOL_TYPES } from '../../../store/useToolStore';
import { generateStoryboardCodeAsync, generateStoryboardCode } from '../../../utils/codeGenerators';
import StoryboardConverterPreview from './StoryboardConverterPreview';
import StoryboardConverterControls from './StoryboardConverterControls';

const StoryboardConverter = () => {
  const config = useToolStore((state) => state.toolConfigs[TOOL_TYPES.STORYBOARD_CONVERTER]);
  const updateConfig = useToolStore((state) => state.updateToolConfig);
  const setGeneratedFiles = useToolStore((state) => state.setGeneratedFiles);
  const setGeneratedCode = useToolStore((state) => state.setGeneratedCode);
  const setIsGeneratingCode = useToolStore((state) => state.setIsGeneratingCode);
  const setGenerationError = useToolStore((state) => state.setGenerationError);
  const isGeneratingCode = useToolStore((state) => state.isGeneratingCode);
  const generationError = useToolStore((state) => state.generationError);

  useEffect(() => {
    const generateCode = async () => {
      if (!config.storyboardContent || !config.storyboardContent.trim()) {
        setGeneratedFiles([]);
        setGeneratedCode('');
        setGenerationError(null);
        return;
      }

      setIsGeneratingCode(true);
      setGenerationError(null);

      try {
        // Use Python backend for deterministic conversion
        const files = await generateStoryboardCodeAsync({
          ...config
        });
        
        setGeneratedFiles(files);
        
        // Set the first file's content as generatedCode for backward compatibility
        if (files.length > 0) {
          setGeneratedCode(files[0].content);
        } else {
          setGeneratedCode('');
        }
      } catch (error) {
        console.error('Error generating code:', error);
        setGenerationError(error.message || 'Failed to generate code. Please try again.');
        
        // Fallback: try basic conversion
        try {
          const fallbackFiles = generateStoryboardCode(config);
          setGeneratedFiles(fallbackFiles);
          if (fallbackFiles.length > 0) {
            setGeneratedCode(fallbackFiles[0].content);
          }
        } catch (fallbackError) {
          console.error('Fallback conversion also failed:', fallbackError);
          setGeneratedFiles([]);
          setGeneratedCode('');
        }
      } finally {
        setIsGeneratingCode(false);
      }
    };

    generateCode();
  }, [config, setGeneratedFiles, setGeneratedCode, setIsGeneratingCode, setGenerationError]);

  const handleConfigChange = (newConfig) => {
    updateConfig(TOOL_TYPES.STORYBOARD_CONVERTER, newConfig);
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Storyboard → SwiftUI Converter</h2>
      {isGeneratingCode && (
        <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 dark:border-blue-400"></div>
            <span className="text-blue-800 dark:text-blue-300 text-sm">Generating SwiftUI code...</span>
          </div>
        </div>
      )}
      {generationError && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
          <p className="text-red-800 dark:text-red-300 text-sm">{generationError}</p>
        </div>
      )}
      <StoryboardConverterControls config={config} onChange={handleConfigChange} />
    </div>
  );
};

export const StoryboardConverterPreviewComponent = () => {
  const config = useToolStore((state) => state.toolConfigs[TOOL_TYPES.STORYBOARD_CONVERTER]);
  return <StoryboardConverterPreview config={config} />;
};

export default StoryboardConverter;

